import { Check, X } from 'lucide-react';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { PLAN_DEFINITIONS } from '@/api-types';
import type { PlanLimits } from '@/api-types';

const tiers = ['free', 'pro', 'team', 'enterprise'] as const;

interface FeatureRow {
	label: string;
	values: Record<typeof tiers[number], string | boolean>;
}

function formatLimitValue(value: number | null | boolean): string | boolean {
	if (typeof value === 'boolean') return value;
	if (value === null) return 'Unlimited';
	return value.toLocaleString();
}

const limitFeatures: Array<{ label: string; key: keyof PlanLimits }> = [
	{ label: 'Builds per month', key: 'max_builds' },
	{ label: 'Iterations per month', key: 'max_iterations' },
	{ label: 'Hosted apps', key: 'max_hosted_apps' },
	{ label: 'Team members', key: 'max_members' },
	{ label: 'Custom domains', key: 'custom_domains' },
	{ label: 'Slack & Discord', key: 'slack_discord_integrations' },
	{ label: 'Complex app hosting', key: 'complex_app_hosting' },
	{ label: 'Deploy to own Cloudflare', key: 'deploy_to_own_cf' },
	{ label: 'Overage billing', key: 'allows_overages' },
];

const featureRows: FeatureRow[] = limitFeatures.map(({ label, key }) => ({
	label,
	values: Object.fromEntries(
		tiers.map((tier) => [tier, formatLimitValue(PLAN_DEFINITIONS[tier].limits[key])])
	) as FeatureRow['values'],
}));

function CellValue({ value }: { value: string | boolean }) {
	if (typeof value === 'boolean') {
		return value
			? <Check className="h-4 w-4 text-green-500 mx-auto" />
			: <X className="h-4 w-4 text-text-tertiary mx-auto" />;
	}
	return <span className="text-sm">{value}</span>;
}

export function PlanComparison() {
	return (
		<div className="overflow-x-auto">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead className="w-[200px]">Feature</TableHead>
						{tiers.map((tier) => (
							<TableHead key={tier} className="text-center">
								{PLAN_DEFINITIONS[tier].name}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{featureRows.map((row) => (
						<TableRow key={row.label}>
							<TableCell className="font-medium text-sm">{row.label}</TableCell>
							{tiers.map((tier) => (
								<TableCell key={tier} className="text-center">
									<CellValue value={row.values[tier]} />
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}
